/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { AutoRefresh, CustomButtonGroup, CustomHighCharts, DashboardHeader, DashboardLoader, DashboardTheme, FilterLayout, useAppDispatch, useAppSelector } from '../../../globalUtils/globalExports';
import './SoftwareReleaseDashboard.css';
import ChargerOnVersionTrend from './ChargerOnVersionTrend';
import DeploymentProgressTend from './DeploymentProgressTrend';
import { fetchVersionReleasesSoftwarVersionReferenceData, getFilterSelectedValue, getVersionReleasesReducer, fetchChargerOnVersionTrendList, fetchLatestVersionInfoData, fetchDeploymentProgressTrendList, fetchVersionReleasesDateRangeReferenceData, getCurrentFiltersValues, getReferenceDataReducer, fetchChargerProductTypeReferenceData, setFilterData, setRedirectedStateForDeploymentLogsFilters, fetchVersionProductSubTypeReferenceData, fetchVersionSwTypeReferenceData, fetchCustomerReferenceData } from '../../../rmsReduxStore/reduxExports';
import { useEffectOnce } from '../../../globalUtils/globalHooks';
import { Options } from 'highcharts';
import { VersionStatsCards } from './VersionStatsCards';
import { Icon,IconWithTooltip } from '../../../globalUtils/globalIcons';
import { setRedirectedStateForChargerFilters } from '../../../rmsReduxStore/chargersRedux/chargerCreators';
import { useNavigate } from 'react-router';
import CustomChartPopup from './CustomChartPopup';
import CustomPulseLoader from './CustomPulseLoader';
import { Menu, MenuItem } from '@mui/material';
import { Id, toast } from 'react-toastify';



function SoftwareReleaseDashboard() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [showDashboardLoader, setShowDashboardLoader] = useState(false)
  const { chargerProductTypeReferenceData,versionReleasesProductSubTypeReferenceData,versionReleasesSwTypeReferenceData,customerReferenceData } = useAppSelector(getReferenceDataReducer)
  const productTypeFilter = getFilterSelectedValue('productType', 'versionReleasesDashboard', false);
  const versionReleaseTypeFilter = getFilterSelectedValue('swVersionReleaseType','versionReleasesDashboard',false);
  const productSubTypeFilter = getFilterSelectedValue('swVersionProductSubType','versionReleasesDashboard',false);
  const versionSwTypeFilter = getFilterSelectedValue('versionSwType','versionReleasesDashboard',false);
  const [selectedProductType, setSelectedProductType] = useState(productTypeFilter?.[0]?.name);
  const { latestVersionInfo, latestVersionInfoLoader } = useAppSelector(getVersionReleasesReducer);
  const [expandDeploymentChart, setExpandDeploymentChart] = useState<boolean>(false);
  const [expandChargerOnVersionChart, setExpandChargerOnVersionChart] = useState<boolean>(false);
  const [versionReleaseType,setVersionReleaseType] = useState<string>(versionReleaseTypeFilter?.[0]?.name);
  const [productSubType,setProductSubType] = useState<string>(productSubTypeFilter?.[0]?.name);
  const [productSubTypeBtnEl, setProductSubTypeBtnEl] = useState(null);
  const [showProductSubTypeList,setShowProductSubTypeList] = useState(false);

  //Currently Applicable only for Harmony Gen2.0. It can be Upper and CCU.
  const [versionSwType,setVersionSwType] = useState(versionSwTypeFilter?.[0]?.name);


  const errorToast = (message):Id => {
    toast.dismiss();
    return toast.error(message)
  };


  const dashboardCards = useMemo(() => {
    let customersToInclude = getFilterSelectedValue('customer','',true);
    if(!customersToInclude || customersToInclude?.length === 0){
      customersToInclude = customerReferenceData?.filter(customer=> customer?.customerType==='External' &&  customer?.name!=='All')?.map(customer=>({
            id: customer?.id,
            value: customer?.value,
            label: customer?.label,
            name: customer?.name
         }))
    }
    return [
      {
        containerClassName: 'version__stats__container-left-bottom-left',
        cardClassName: 'total-chargers',
        header: 'Total Chargers',
        headerTooltipText:'Chargers belonging to the selected product type. Click the number to view the list of chargers.',
        field: 'totalChargers',
        iconClassName: 'software__version__releases__dashboard__total__chargers__icon',
        onClick: async () => {
          await setFilterData(null, '', 'chargers', 'CLEAR', false)(dispatch);
          setRedirectedStateForChargerFilters(true, null, null, null, 'versionReleasesDashboard', customersToInclude, latestVersionInfo?.productType, null)(dispatch).then(res => {
            if (res === 'Action dispatched successfully') {
              navigate('/manage-chargers');
            }
          });
        }
      },
      {
        containerClassName: 'version__stats__container-left-bottom-right',
        cardClassName: 'outdated-chargers',
        header: 'Outdated Chargers',
        headerTooltipText:'Chargers that are not on the latest software version (total chargers - updated chargers). Click the number to view the list of chargers.',
        field: 'outdatedChargers',
        iconClassName: 'software__version__releases__dashboard__outdated__chargers__icon',
        onClick: async () => {
          await setFilterData(null, '', 'chargers', 'CLEAR', false)(dispatch);
          const isCCU = selectedProductType === 'Harmony Gen2.0' && versionSwType === 'CCU';
          setRedirectedStateForChargerFilters(true, null, null, null, 'versionReleasesDashboard', customersToInclude, latestVersionInfo?.productType, latestVersionInfo?.latestVersion, true,isCCU)(dispatch).then(res => {
            if (res === 'Action dispatched successfully') {
              navigate('/manage-chargers');
            }
          });
        }
      }
    ]
  }, [latestVersionInfo])

  useEffectOnce(() => {
    dispatch(fetchChargerProductTypeReferenceData());
    dispatch(fetchVersionReleasesDateRangeReferenceData());
    dispatch(fetchVersionProductSubTypeReferenceData());
    dispatch(fetchVersionSwTypeReferenceData());
    dispatch(fetchCustomerReferenceData());
  })

  const onRefeshCallBack = useCallback(() => {
    const dashboardCurrentFilters = getCurrentFiltersValues('versionReleasesDashboard');
    fetchDashboardData({ filters: dashboardCurrentFilters },false)
  }, [getCurrentFiltersValues, fetchLatestVersionInfoData, selectedProductType])



  const formatSimpleDate = (inputDate: string,textToDisplay:string): string => {
    // Take only date part (ignore time completely)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const givenDate = new Date(inputDate);
    givenDate.setHours(0, 0, 0, 0);
    // Difference in days
    const diffInMs = today.getTime() - givenDate.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    if (diffInDays === 0) {
      return `${textToDisplay} Today`;
    } else if (diffInDays === 1) {
      return `${textToDisplay} Yesterday`;
    } else if (diffInDays > 1 && diffInDays <= 5) {
      // Show weekday name
      return `${textToDisplay} on ${givenDate.toLocaleDateString('en-US', { weekday: 'long' })}`;
    } else {
      // Older → show date (dd/mm/yyyy)
      return `${textToDisplay} on ${givenDate.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })}`;
    }
  };


  const getFilteredFilters = (widget: string, filters: any) => {
    const filterKeyList = ['customer', 'productType'];
    switch (widget) {
      case 'chargerOnVersionTrend':
        filterKeyList.push('softwareVersion');
        break;
      case 'deploymentProgressTrend':
        filterKeyList.push('swVersionDateInterval');
        break;
      default:
        break;
    }
    return filters?.filter(item => filterKeyList.includes(item.key))
  }

  const fetchFilterSelectedValues = (filterKey: string) => {
    return getFilterSelectedValue(filterKey, 'versionReleasesDashboard', false);
  }
  const fetchChargerOnVersionTrendData: any = async (filters: any) => {
    const filterList = getFilteredFilters('chargerOnVersionTrend', filters);
    const productTypeFilter = filterList.filter(item => item.key === 'productType')?.[0];
    const productTypeRef = chargerProductTypeReferenceData ? chargerProductTypeReferenceData : JSON.parse(await dispatch(fetchChargerProductTypeReferenceData()))
    const productType = productTypeRef?.filter(item => item.id === productTypeFilter?.values?.[0])?.[0]?.name
    const filterToSend = filterList?.filter(item => item.key !== 'productType')
    
    //Subtype filter applicable only to Product Type 1(Harmony Gen1.0')
    const productSubType = productType==='Harmony Gen1.0' ? filters?.filter(item=>item.key === 'swVersionProductSubType')?.[0]?.values?.[0] : '';

    // swType filter applicable only to Product Type 3(Harmony Gen2.0)
    const swTypeFilter = productType === 'Harmony Gen2.0' ? filters?.filter(item=>item.key === 'versionSwType')?.[0]?.values?.[0] : ''; 
    dispatch(fetchChargerOnVersionTrendList({ productType: productType, filters: filterToSend,productSubType,versionSwType:swTypeFilter}));
  }

  const fetchDeploymentProgressTrendData: any = useCallback(async (filters: any) => {
    const productTypeFilter = filters.filter(item => item.key === 'productType')?.[0];
    const productTypeRef = chargerProductTypeReferenceData ? chargerProductTypeReferenceData : JSON.parse(await dispatch(fetchChargerProductTypeReferenceData()))
    const productType = productTypeRef?.filter(item => item.id === productTypeFilter?.values?.[0])?.[0]?.name
    const filterList = getFilteredFilters('deploymentProgressTrend', filters);
    //Subtype filter applicable only to Product Type 1(Harmony Gen1.0')
    const productSubType = productType==='Harmony Gen1.0' ? filters?.filter(item=>item.key === 'swVersionProductSubType')?.[0]?.values?.[0] : '';

    // swType filter applicable only to Product Type 3(Harmony Gen2.0)
    const swTypeFilter = productType === 'Harmony Gen2.0' ? filters?.filter(item=>item.key === 'versionSwType')?.[0]?.values?.[0] : ''; 
    dispatch(fetchDeploymentProgressTrendList({ filters: filterList,productSubType,versionSwType:swTypeFilter}))
  }, [chargerProductTypeReferenceData])

  const fetchDashboardData = useCallback(async (val,excludeGraphCalls) => {
    const filterList = val?.filters;
    const productTypeFilter = filterList.filter(item => item.key === 'productType')?.[0];
    const productTypeRef = chargerProductTypeReferenceData ? chargerProductTypeReferenceData : JSON.parse(await dispatch(fetchChargerProductTypeReferenceData()))
    const productType = productTypeRef?.filter(item => item.id === productTypeFilter?.values?.[0])?.[0]?.name
    const releaseType = val?.filters?.filter(item=>item.key === 'swVersionReleaseType')?.[0]?.values?.[0]; 
    //Subtype filter applicable only to Product Type 1(Harmony Gen1.0')
    const productSubType = productType==='Harmony Gen1.0' ? val?.filters?.filter(item=>item.key === 'swVersionProductSubType')?.[0]?.values?.[0] : '';

    // swType filter applicable only to Product Type 3(Harmony Gen2.0)
    const swTypeFilter = productType === 'Harmony Gen2.0' ? val?.filters?.filter(item=>item.key === 'versionSwType')?.[0]?.values?.[0] : ''; 

    dispatch(fetchLatestVersionInfoData({ 'productType': productType, filters: val?.filters.filter(item => item.key === 'customer'),customerRelease:releaseType,productSubType,versionSwType:swTypeFilter}))
    if(!excludeGraphCalls){
      fetchChargerOnVersionTrendData(val?.filters)
      fetchDeploymentProgressTrendData(val?.filters,productType)
    }
  }, [fetchLatestVersionInfoData, fetchChargerOnVersionTrendData, fetchDeploymentProgressTrendData])


  useEffect(() => {
    dispatch(fetchVersionReleasesSoftwarVersionReferenceData({ 'productType': selectedProductType }))
  }, [selectedProductType])

  const getFormattedData = useCallback(() => {
    if (!latestVersionInfo) return [];
    const isAllZero = latestVersionInfo?.updatedChargers === 0 && latestVersionInfo?.pendingChargers === 0 && latestVersionInfo?.failedChargers === 0
    const filteredData = [
      { name: 'Updated Chargers', value: isAllZero ? 30 : latestVersionInfo?.updatedChargers, color: '#00A88F', count: latestVersionInfo?.updatedChargers },
      { name: 'Pending Chargers', value: isAllZero ? 30 : latestVersionInfo?.pendingChargers, color: '#F2B90C', count: latestVersionInfo?.pendingChargers },
      { name: 'Failed Chargers', value: isAllZero ? 30 : latestVersionInfo?.failedChargers, color: '#F65A5A', count: latestVersionInfo?.failedChargers },
    ]
    return filteredData;
  }, [latestVersionInfo])

  const filteredData = getFormattedData()
  const versionStatsChartOptions: Options = useMemo(() => {
    return {
      chart: {
        height: 250,
        backgroundColor: 'transparent',
        events: {
          load: function (): void {
            this.reflow();
          },
          render: function (): void {
            const chart = this as Highcharts.Chart & any;

            // Remove previous labels
            chart.customLabel?.destroy?.();

            // Add new label in the center
            const text = latestVersionInfo?.targettedChargers;

            // Create a temporary label to measure its size
            const tempLabel = chart.renderer.label(text)
              .css({ fontSize: '40px', textAlign: 'center' })
              .add();

            const bbox = tempLabel.getBBox(); // get width and height

            tempLabel.destroy(); // remove temporary

            // Add the label at the center
            chart.customLabel = chart.renderer.label(text)
              .css({ fontSize: '40px', textAlign: 'center',cursor:'pointer' })
              .attr({
                zIndex: 5
              })
              .add();

            // Position it correctly
            chart.customLabel.attr({
              x: chart.plotLeft + chart.plotWidth / 2 - bbox.width / 2,
              y: chart.plotTop + chart.plotHeight / 2 - bbox.height / 2
            });

            chart.customLabel.on('click',()=>{
              if(!latestVersionInfo?.latestVersion || latestVersionInfo?.latestVersion?.trim() === '') {
                errorToast('No data found.')
                return;
              }
              const customerFilter = getFilterSelectedValue('customer','',true);
              setRedirectedStateForDeploymentLogsFilters(true)(dispatch)
              navigate('/manage-deployment-logs', {
                state: {
                  productType: latestVersionInfo?.productType,
                  softwareVersion: latestVersionInfo?.latestVersion,
                  customer:customerFilter
                }
              })
            })

          }
        }
      },
      title: {
        text: '',
        align: 'center',
      },
      legend: {
        enabled: false,
      },
      exporting: { enabled: false },
      tooltip: {
        enabled: true,
        valueDecimals: 2,
        valueSuffix: ' %',
        formatter: function () {
          const tooltipText = `<span> <b>${this.point.name} : </b> ${(this.point as any)?.count} Chargers </span>`;
          return tooltipText;
        }
      },
      credits: {
        enabled: false,
      },
      plotOptions: {
        pie: {
          borderWidth: 0,
          colorByPoint: true,
          type: 'pie',
          size: '100%',
          innerSize: '80%',
          dataLabels: {
            enabled: false
          },
        },
      },
      //colors: ['#3b85bf','#f3b5aa', '#deecf7'],
      series: [
        {
          type: 'pie',
          name: '6',
          // data: [{name: connectivityMediumData?.[0].network, y: connectivityMediumData?.[0].percentage, chargerCount: connectivityMediumData?.[0].count},
          // {name: connectivityMediumData?.[1].network, y: connectivityMediumData?.[1].percentage, chargerCount: connectivityMediumData?.[1].count}]
          data: filteredData.map(entry => ({
            name: entry.name,
            y: entry.value,
            color: entry.color,
            count: entry.count
          }))
        },
      ],
    };
  }, [filteredData]);


  const handleFilterDataChange = useCallback(async (val, filterKey, isGlobal) => {
    const res = await setFilterData(val, filterKey, 'versionReleasesDashboard', 'SET', isGlobal ? isGlobal : false)(dispatch)
    if (res?.message === 'Action dispatched successfully') {
      fetchDashboardData({ filters: res?.filters },filterKey==='swVersionReleaseType')
    }
  }, [dispatch, setFilterData, fetchDashboardData])


  const handleproductTypeBtnClick = async (val: string) => {
    const productTypeFilter = chargerProductTypeReferenceData?.filter(item => item.name === val);
    const res = await setFilterData([], 'softwareVersion', 'versionReleasesDashboard', 'SET', false)(dispatch);
    if (res?.message === 'Action dispatched successfully') {
      handleFilterDataChange(productTypeFilter, 'productType', false);
      setSelectedProductType(val);
    }
  }

  const handleReleaseTypeBtnClick = async(val:string)=>{
    const releaseTypeFilter = {id:val,name:val,label:val,value:val};
    handleFilterDataChange(releaseTypeFilter,'swVersionReleaseType',false);
    setVersionReleaseType(val);
  }

  const handleProductSubTypeChange = async(val:string)=>{
    const productSubTypeFilter = versionReleasesProductSubTypeReferenceData?.filter(item => item.name === val);
      handleFilterDataChange(productSubTypeFilter, 'swVersionProductSubType', false);
      setProductSubType(val);
  }

  const handleSwTypeChange = async(val:string)=>{
    const swTypeFilter = versionReleasesSwTypeReferenceData?.filter(item=>item.name === val);
    handleFilterDataChange(swTypeFilter,'versionSwType',false);
    setVersionSwType(val);
  }

  const headerActionBtns = useMemo(() => [
    {
      buttonText: 'EVM150',
      btnClassName: 'software__releases_action_btn',
      handleClick: () => { handleproductTypeBtnClick('EVM150') },
      buttonId: (selectedProductType === 'EVM150') ? 'active__software__releases_action_btn-type2' : 'software__releases_action_btn-type2',
      buttonVariant: 'outline',
      handleKeyPress: () => { }
    },
    {
      buttonText:`Harmony Gen1.0 - ${productSubType?.split(' ')?.[0]==='All' ? 'All' : productSubType?.split(' ')?.[0]+'"'}`,
      btnClassName: 'software__releases_action_btn',
      handleClick: (e) => { 
        if(selectedProductType==='Harmony Gen1.0'){
          setShowProductSubTypeList(true);
          setProductSubTypeBtnEl(e.currentTarget)
        }
        handleproductTypeBtnClick('Harmony Gen1.0') },
      buttonId: (selectedProductType === 'Harmony Gen1.0') ? 'active__software__releases_action_btn-type1' : 'software__releases_action_btn-type1',
      buttonVariant: 'outline',
      customEndIcon:(selectedProductType==='Harmony Gen1.0') ? showProductSubTypeList ? 'rms__up-white-arrow' : 'rms__down-white-arrow': 'rms__down-black-arrow',
      handleKeyPress: () => { }
    },
    {
      buttonText: 'Harmony Gen2.0',
      btnClassName: 'software__releases_action_btn',
      handleClick: () => { handleproductTypeBtnClick('Harmony Gen2.0') },
      buttonId: (selectedProductType === 'Harmony Gen2.0') ? 'active__software__releases_action_btn-type3' : 'software__releases_action_btn-type3',
      buttonVariant: 'outline',
      handleKeyPress: () => { }
    },
  ], [selectedProductType, setSelectedProductType, handleproductTypeBtnClick])

  const versionReleasesTypeToggleBtns = [
    {
      icon: (versionReleaseType==='Enabled') ? 'software__release__dashbaord__customer-release-icon-active' : 'software__release__dashbaord__customer-release-icon',
      tooltipText:'Customer release',
      btnId: 'software__release__dashbaord-customer-release-btn',
      btnClassName: (versionReleaseType==='Enabled') ? 'software__release__dashbaord__customer-release-active' : 'software__release__dashbaord__customer-release',
      handleClick: ()=>{handleReleaseTypeBtnClick('Enabled')}
    },
    {
      icon: (versionReleaseType==='Disabled') ? 'software__release__dashbaord__internal-release-icon-active':'software__release__dashbaord__internal-release-icon',
      tooltipText:'Internal testing release',
      btnId: 'software__release__dashbaord-internal-release-btn',
      btnClassName: (versionReleaseType==='Disabled') ? 'software__release__dashbaord__internal-release-active' : 'software__release__dashbaord__internal-release',
      handleClick: ()=>{handleReleaseTypeBtnClick('Disabled')}
    },
  ]

  const swTypeToggleBtns = [
    {
      headerName:'Upper',
      btnId:'software__release__dashbaord-sw-type-upper-btn',
      btnClassName: (versionSwType==='Upper') ? 'software__release__dashbaord__sw-type-active' : 'software__release__dashbaord__sw-type-inactive',
      handleClick: ()=>{handleSwTypeChange('Upper')}
    },
    {
      headerName:'CCU',
      btnId:'software__release__dashbaord-sw-type-ccu-btn',
      btnClassName:(versionSwType==='CCU') ? 'software__release__dashbaord__sw-type-active' : 'software__release__dashbaord__sw-type-inactive',
      handleClick: ()=>{handleSwTypeChange('CCU')}
    }
  ]



  return (
    <ThemeProvider theme={DashboardTheme}>
      <DashboardLoader handleClose={setShowDashboardLoader} showLoader={showDashboardLoader} />
      <div className='software__release__dashboard software__releases__dashboard__container'>
        <div className='software__releases__dashboard__header__container'>
          <div className='software__releases__dashboard__header__container-left'>
            <div className='software__releases__dashboard__header__heading'>
              <DashboardHeader header='SW Dashboard' headerClassName='software__release__dashboard_header' />
            </div>
            <div className="software__releases__dashboard__header-left">
              <div className="software__releases__dashboard__header-bottom-auto-refresh-wrap">
                <AutoRefresh
                  refreshDate={latestVersionInfo?.lastRefreshTime ?? ''}
                  containerClassName="software__releases__dashboard__auto__refresh__component"
                  onRefreshCallback={onRefeshCallBack}
                  pathName="/software-version-release-dashboard"
                  refreshLocation={true}
                />
              </div>
            </div>
          </div>

          <div className='software__releases__dashboard__header__container-right'>
            <div className='software__releases__dashboard__header-right'>
              {
                selectedProductType === 'Harmony Gen2.0' && <div className='software__releases__dashboard__header-right-sw-type-filters'>
                    {
                      swTypeToggleBtns.map(btn=>{
                        return (
                          <div key={btn.btnId} 
                          onClick={btn.handleClick}
                          className={btn.btnClassName}>
                            {btn.headerName}
                          </div>
                        )
                      })
                    }
                </div>
              }
              <div className='software__releases__dashboard__header-right-dropdown-filters'>
                <FilterLayout requiredReferenceDataCallbacks={[]} handleFilterDataChange={handleFilterDataChange}
                  filters={[]} screen='versionReleasesDashboard' excludeGlobalFilters={false} />
              </div>
              <div className='software__releases__dashboard__header-right-btn-filters'>
                <CustomButtonGroup buttonsList={headerActionBtns} buttonGroupClassName='software__releases__header__action__btns' />
              </div>
            </div>
          </div>
        </div>


        <div className='software__releases__dashboard__body__container'>
          {/* Version Info Cards for Selected Product Type */}
          <div className='version__stats__container'>
            <div className='version__stats__container-left'>
              {(latestVersionInfoLoader || !latestVersionInfo) ? <div 
              className='version__stats__container-left-loader'
              style={{ height: '320px', border: '1px solid #E1E4EA', borderRadius: '20px' }}>
                <CustomPulseLoader showLoader={true} size={20} />
              </div> : <>
                <div className='version__stats__container-left-top'>
                  <div style={{ position: 'absolute', zIndex: 999, scale: 0.9, right: 0, top: 0 }}>
                    <Icon
                      containerClassName="card__background-icon"
                      iconClassName="software__version__releases__dashboard__header__icon"
                    />
                  </div>
                  <div className='version__stats__version__info-card'>
                    <div className='version__stats__version__info-card-header'>
                      <div className='version__stats__version__info-card-header-heading'>
                        Latest Software Version
                      </div>
                      <div className='version__stats__version__info-card-header-version-release-type'>
                         {
                          versionReleasesTypeToggleBtns.map(btn=>{
                            return (
                              <div key={btn?.btnId} className={btn?.btnClassName} 
                                onClick={btn?.handleClick}>
                                <IconWithTooltip iconClassName={btn?.icon} toolTipText={btn?.tooltipText} containerClassName={`${btn?.btnClassName}-inner-icon-container`}/>
                              </div>
                            )
                          })
                         }
                      </div>
                    </div>
                    <div className='version__stats__version__info-card-body'>
                      <div className='version__stats__version__info-card-body-left'>
                        <div className='version__stats__version__info-card-body-left-dates-container'>
                          <div className='version__stats__version__info-card-body-left-release-date'>
                            {latestVersionInfo?.releaseDate ? formatSimpleDate(latestVersionInfo?.releaseDate as string, 'Released') : 'Released on  -'}
                          </div>
                          <div className='data-separator-smaller'></div>
                          <div className='version__stats__version__info-card-body-left-deployed-date'>
                            {latestVersionInfo?.firstDeployedOn ? formatSimpleDate(latestVersionInfo?.firstDeployedOn as string,'1st Deployed') : '1st Deployed on  -'}
                          </div>     
                        </div>
                        <div className='version__stats__version__info-card-body-left-software-version'>
                          {latestVersionInfo?.latestVersion && latestVersionInfo?.latestVersion!='' ? latestVersionInfo?.latestVersion : 'N/A'}
                        </div>
                      </div>
                      <div className='version__stats__version__info-card-body-right'>
                        <div className='version__stats__version__info-card-body-right-severity'>
                          <div className='version__stats__version__info-card-body-right-severity-label'>
                            Severity
                          </div>
                          <div className='version__stats__version__info-card-body-right-severity-value'>
                            {latestVersionInfo?.severity && latestVersionInfo?.severity !='' ? latestVersionInfo?.severity : '-'}
                          </div>
                        </div>
                        <div className='data-separator'></div>
                        <div className='version__stats__version__info-card-body-right-classification'>
                          <div className='version__stats__version__info-card-body-right-classification-label'>
                            Classification
                          </div>
                          <div className='version__stats__version__info-card-body-right-classification-value'>
                            <Icon
                              containerClassName={'card__header__icon'} iconClassName={`software__version__releases__dashboard__classification__icon-${latestVersionInfo?.classification?.toLowerCase()}`}
                            />
                            {latestVersionInfo?.classification && latestVersionInfo?.classification!='' ? latestVersionInfo?.classification  :  '-'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
                <div className='version__stats__container-left-bottom'>
                  {
                    dashboardCards.map(card => {
                      return <div key={card.containerClassName} className={card.containerClassName}
                        onClick={card.onClick}>
                        <div style={{ position: 'absolute', zIndex: 998, scale: 0.9, right: 0,cursor:'pointer' }}>
                          <Icon
                            containerClassName="card__background-icon"
                            iconClassName="software__version__releases__dashboard__header__icon"
                          />
                        </div>

                        <div
                          key={card.cardClassName}
                          className={'card ' + card.cardClassName}
                          onClick={async (e) => { }}
                        >
                          <div className="card__header">
                            <Icon
                              containerClassName={'card__header__icon'} iconClassName={card.iconClassName}
                            />
                            <div className="card__header__text">
                              {card.header}
                              <IconWithTooltip iconClassName="rms_info_icon-black" toolTipText={card?.headerTooltipText} containerClassName={card.cardClassName + '-header-tooltip'}  />
                            </div>
                          </div>
                          <div className="card__footer__wrap">
                            <div className="card__header__count">
                              {latestVersionInfo?.[card.field] ?? 0}
                            </div>
                          </div>
                        </div>
                      </div>
                    })
                  }
                </div>
              </>
              }
            </div>

            <div className='version__stats__container-right'>
              {(latestVersionInfoLoader || !latestVersionInfo) ? <div  className='version__stats__container-right-loader' style={{ height: '300px' }}>
                <CustomPulseLoader showLoader={true} size={20} />
              </div> : <>
                <div className='version__stats__container-right-heading'>
                  Targetted Chargers
                  <IconWithTooltip iconClassName="rms_info_icon-black" toolTipText={'Chargers for which software deployment has been attempted via the Software Release module.'} containerClassName={'version__stats__container-right-header-tooltip'}  />
                </div>
                <div className='version__stats__container-right-body'>
                  {
                    latestVersionInfo && filteredData.length > 0 && <>
                      <div className='version__stats__container-right-body-pie-chart'>
                        <CustomHighCharts
                          highChartOptions={versionStatsChartOptions}
                        />
                      </div>
                      <div className='version__stats__container-right-body-count-cards'>
                        <div className='version__stats__container-right-body-count-cards-top'>
                          <VersionStatsCards cardId={'version-stats-updated-card'} headerText={'UPDATED'} count={latestVersionInfo?.updatedChargers}
                            selectedCard={''}
                            headerTooltipText = {'Indicates chargers that have the latest software version. Click the number to view list of updated chargers.'}
                            onCardClick={async () => {
                              if(!latestVersionInfo?.latestVersion || latestVersionInfo?.latestVersion?.trim() === '') {
                                  errorToast('No data found.')
                                  return;
                              }
                              let customerFilter = getFilterSelectedValue('customer','',true);
                              if(!customerFilter || customerFilter?.length === 0){
                                customerFilter = customerReferenceData?.filter(customer=> customer?.customerType==='External' &&  customer?.name!=='All')?.map(customer=>({
                                    id: customer?.id,
                                    value: customer?.value,
                                    label: customer?.label,
                                    name: customer?.name
                                }))
                              }
                              await setFilterData(null, '', 'chargers', 'CLEAR', false)(dispatch);
                              const isCCU = selectedProductType === 'Harmony Gen2.0' && versionSwType === 'CCU';
                              setRedirectedStateForChargerFilters(true, null, null, null, 'versionReleases', customerFilter, latestVersionInfo?.productType, latestVersionInfo?.latestVersion, false,isCCU)(dispatch).then(res => {
                                if (res === 'Action dispatched successfully') {
                                  navigate('/manage-chargers');
                                }
                              });
                            }}
                          />
                          <VersionStatsCards cardId={'version-stats-pending-card'} headerText={'PENDING'} count={latestVersionInfo?.pendingChargers}
                            headerTooltipText={'Unique chargers awaiting deployment due to scheduling or a delay in response from the charger upon deployment. Click the number to view deployment logs.'}
                            selectedCard={''}
                            onCardClick={() => {
                              if(!latestVersionInfo?.latestVersion || latestVersionInfo?.latestVersion?.trim() === '') {
                                  errorToast('No data found.')
                                  return;
                              }
                              const customerFilter = getFilterSelectedValue('customer','',true)
                              setRedirectedStateForDeploymentLogsFilters(true)(dispatch)
                              navigate('/manage-deployment-logs', {
                                state: {
                                  productType: latestVersionInfo?.productType,
                                  softwareVersion: latestVersionInfo?.latestVersion,
                                  swLogStatus: 'Pending',
                                  customer:customerFilter
                                }
                              })
                            }}
                          />
                        </div>
                        <div className='version__stats__container-right-body-count-cards-bottom'>
                          <VersionStatsCards cardId={'version-stats-failed-card'} headerText={'FAILED'} count={latestVersionInfo?.failedChargers}
                            headerTooltipText={'Unique chargers with deployment failures. Click the number to view deployment logs.'}
                            selectedCard={''}
                            onCardClick={() => {
                              if(!latestVersionInfo?.latestVersion || latestVersionInfo?.latestVersion?.trim() === '') {
                                  errorToast('No data found.')
                                  return;
                              }
                              const customerFilter = getFilterSelectedValue('customer','',true)
                              setRedirectedStateForDeploymentLogsFilters(true)(dispatch)
                              navigate('/manage-deployment-logs', {
                                state: {
                                  productType: latestVersionInfo?.productType,
                                  softwareVersion: latestVersionInfo?.latestVersion,
                                  swLogStatus: 'Failed',
                                  customer:customerFilter
                                }
                              })
                            }} />
                        </div>
                      </div>

                    </>

                  }
                </div>
              </>
              }
            </div>

          </div>


          {/* Version Graphs */}
          <div className='software__releases__dashboard__charts'>
            <div className='software__releases__dashboard__charts-charger-count'>
              <ChargerOnVersionTrend handleFilter={fetchChargerOnVersionTrendData} getFilterValue={fetchFilterSelectedValues} popupHandler={setExpandChargerOnVersionChart} />
            </div>
            <div className='software__releases__dashboard__charts-deployment-progress'>
              <DeploymentProgressTend handleFilter={fetchDeploymentProgressTrendData} getFilterValue={fetchFilterSelectedValues} popupHandler={setExpandDeploymentChart} />
            </div>
          </div>
        </div>
      </div>

      <CustomChartPopup
        show={expandChargerOnVersionChart}
        handleClose={() => setExpandChargerOnVersionChart(false)}
      >
        <ChargerOnVersionTrend handleFilter={fetchChargerOnVersionTrendData} getFilterValue={fetchFilterSelectedValues} fromPopUp={true} popupHandler={setExpandChargerOnVersionChart} />
      </CustomChartPopup>
      <CustomChartPopup
        show={expandDeploymentChart}
        handleClose={() => { setExpandDeploymentChart(false) }}
      >
        <DeploymentProgressTend handleFilter={fetchDeploymentProgressTrendData} getFilterValue={fetchFilterSelectedValues} fromPopUp={true} popupHandler={setExpandDeploymentChart} />
      </CustomChartPopup>
      <Menu
        id="product-subtype-option-dropdown"
        anchorEl={productSubTypeBtnEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        className='product-subtype__option__dropdown'
        sx={{ top: '30px' }}
        open={Boolean(productSubTypeBtnEl)}
        onClose={()=>{
          setShowProductSubTypeList(false);
          setProductSubTypeBtnEl(null);
        }}
      >
        {
          versionReleasesProductSubTypeReferenceData?.map(subtype=>{
           return( <MenuItem key={subtype?.id} selected={subtype?.name === productSubType} onClick={(e) => { 
            handleProductSubTypeChange(subtype?.name);
            }}>
                <div key={subtype?.id} className='product-subtype__btn-option'>
                    {subtype?.name}
                </div>
            </MenuItem>
           )
          })
        }
      </Menu>
    </ThemeProvider>
  )
}

export default SoftwareReleaseDashboard

